// src/shared/components/layout/Aside.jsx
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Skeleton,
  Typography,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router';

import HomeIcon from '@mui/icons-material/Home';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import PaymentsIcon from '@mui/icons-material/Payments';
import LuggageIcon from '@mui/icons-material/Luggage';
import EventNoteIcon from '@mui/icons-material/EventNote';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { IntegrationInstructions } from '@mui/icons-material';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import RuleFolderOutlinedIcon from '@mui/icons-material/RuleFolderOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useAuth } from '@app/providers/AuthProvider';
import useResponsive from '@shared/hooks/useResponsive';
import UserAvatar from './Avatar';

const logoSrc = '/images/logo2.png';

const Aside = ({ mobileOpen, handleDrawerToggle, expanded, onToggleExpand }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();

  // On mobile, "expanded" is implied by `mobileOpen` (drawer is full-width when open).
  // On desktop, the user explicitly controls expand/collapse via the toggle button.
  const isExpanded = isMobile ? mobileOpen : expanded;
  const drawerWidth = isExpanded ? 248 : 76;

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', `${drawerWidth}px`);
  }, [drawerWidth]);
  const roles = user?.roles ?? [];
  const hasRole = (name) => roles.some((r) => r?.name === name);
  const hasAbility = (name) =>
    roles.some((r) => r?.permissions?.some((p) => p?.name === name));

  // Sidebar groups follow a "what you do" mental model, not a role one:
  //   Workspace → your landing
  //   Travel    → booking actions (personal + staff-on-behalf-of)
  //   Operations→ MSI Staff daily transactions / history
  //   Vendor Tools → check-in flow (Vendor role)
  //   Finance   → all money: billing, payments, exceptions, fees
  //   Directory → records you maintain: customers, vendors, hotels, employees
  //   System    → true admin: users, integrations, derby switch
  // Each section is only pushed if it has at least one visible item, so
  // role-narrowing happens at item granularity (not just section).
  const sections = [];
  // MSI Staff shares the Admin permission set (product decision, mirrors
  // RolesAndPermissionsInitSeeder). Rolling MSI Staff into the `isAdmin`
  // sidebar variable is the single-touchpoint way to open every existing
  // `if (isAdmin)` block. If the two roles diverge again in the future,
  // split this — every `isAdmin || isMsiStaff` further down is intentional
  // belt-and-suspenders (harmless once MSI Staff is inside `isAdmin`).
  // `isSuperAdmin` stays a distinct variable because the Data Seeder gate
  // must remain Super-Admin only.
  const isSuperAdmin = hasRole('Super Admin');
  const isMsiStaff = hasRole('MSI Staff');
  const isAdmin = hasRole('Admin') || isSuperAdmin || isMsiStaff;
  const isTravelMgr = hasRole('Travel Manager');
  const isVendor = hasRole('Vendor');

  // 1. Workspace — everyone with a session
  sections.push({
    key: 'workspace',
    label: 'Workspace',
    items: [
      {
        key: 'home',
        label: 'Home',
        icon: <HomeIcon />,
        path: '/user-home',
        exact: true,
        disabled: !user,
        loading: !user,
      },
    ],
  });

  // 2. Travel — all reservation-related actions, both personal and staff-mode
  {
    const items = [];
    // Anyone who can book (everyone except pure Vendor) gets personal booking.
    if (!isVendor || isAdmin || isMsiStaff || isTravelMgr) {
      items.push({
        key: 'booking',
        label: 'Book Reservation',
        icon: <LuggageIcon />,
        path: '/booking',
        match: (p) => p.startsWith('/booking'),
        disabled: !user,
        loading: !user,
      });
      items.push({
        key: 'reservations',
        label: 'My Reservations',
        icon: <EventNoteIcon />,
        path: '/reservations',
        match: (p) => p.startsWith('/reservations'),
        disabled: !user,
        loading: !user,
      });
    }
    // Staff Reservations = "creating reservations on behalf of someone".
    // Used by MSI Staff (call-in bookings) and Travel Managers (team).
    if (isAdmin || isMsiStaff || isTravelMgr) {
      items.push({
        key: 'staff-reservations',
        label: 'Reservations',
        icon: <AssignmentIcon />,
        path: '/staff-reservations',
        match: (p) => p.startsWith('/staff-reservations'),
      });
    }
    if (items.length) sections.push({ key: 'travel', label: 'Travel', items });
  }

  // 3. Operations — Policy Reviews queue (Admin / MSI Staff / Travel Manager).
  //    Legacy "Transactions" and "Transaction History" entries (the old MSI
  //    approval flow against /lodging-transactions and /transactions/history)
  //    were removed; superseded by Policy Reviews + the new booking flow.
  if (hasAbility('view policy reviews')) {
    sections.push({
      key: 'operations',
      label: 'Operations',
      items: [
        {
          key: 'policy-reviews',
          label: 'Policy Reviews',
          icon: <RuleFolderOutlinedIcon />,
          path: '/policy-reviews',
          match: (p) => p.startsWith('/policy-reviews'),
        },
      ],
    });
  }

  // 4. Vendor Tools — was the check-in / folio submission workflow.
  // Removed with the legacy MsiLodgingTransaction feature when the
  // modern Encompass VCC + TreviPay flow replaced manual folio
  // submission. If the Vendor role gets new self-service pages,
  // rebuild this section around them.

  // 5. Finance — money in/out, exceptions, customer fees
  // Travel Managers see a read-only subset (their own customer's batches +
  // payment history). MSI-side roles see the full toolkit including write
  // actions and investigation tools.
  if (isAdmin || isMsiStaff || isVendor || isTravelMgr) {
    const items = [];
    // The /billing dashboard is the MSI "generate batch" workflow (loads
    // candidates, lets staff batch them). Not useful for TMs — they only
    // see invoices MSI has already created.
    if (isAdmin || isMsiStaff) {
      items.push({
        key: 'billing-main',
        label: 'Billing',
        icon: <ReceiptLongIcon />,
        path: '/billing',
        match: (p) => p === '/billing',
      });
    }
    items.push({
      key: 'billing-batches',
      label: isAdmin || isMsiStaff ? 'Billing Batches' : 'Billing',
      icon: <FolderCopyIcon />,
      path: '/billing/batches',
      // Match all /billing/* routes EXCEPT the standalone ones below
      // AND the new billed-reservations investigation tool (it has its
      // own nav entry).
      match: (p) =>
        p.startsWith('/billing/') &&
        p !== '/billing' &&
        p !== '/billing/record-payment' &&
        p !== '/billing/payments' &&
        !p.startsWith('/billing/payments/') &&
        !p.startsWith('/billing/record-payment') &&
        !p.startsWith('/billing/billed-reservations'),
    });
    // Billed Reservations is an MSI investigation tool — variance lookup,
    // not a customer-facing view.
    if (isAdmin || isMsiStaff) {
      items.push({
        key: 'billed-reservations',
        label: 'Billed Reservations',
        icon: <CompareArrowsIcon />,
        path: '/billing/billed-reservations',
        match: (p) => p.startsWith('/billing/billed-reservations'),
      });
    }
    // Record Payment is MSI-only (TMs can't record payments — MSI records
    // what they've received).
    if (isAdmin || isMsiStaff) {
      items.push({
        key: 'record-payment',
        label: 'Record Payment',
        icon: <PaymentsIcon />,
        path: '/billing/record-payment',
        match: (p) => p === '/billing/record-payment' || p.startsWith('/billing/record-payment/'),
      });
    }
    // Payment History — TMs see their own customer's payment history
    // (backend scoping enforced via ScopesToManagerCustomer).
    items.push({
      key: 'payment-history',
      label: 'Payment History',
      icon: <HistoryIcon />,
      path: '/billing/payments',
      match: (p) => p === '/billing/payments' || p.startsWith('/billing/payments/'),
    });
    if (isAdmin || isMsiStaff) {
      items.push({
        key: 'payment-exceptions',
        label: 'Payment Exceptions',
        icon: <ReportProblemOutlinedIcon />,
        path: '/payment-exceptions',
        match: (p) => p.startsWith('/payment-exceptions'),
      });
      // VCC issuance — MSI-only. Lives under Finance now (previously
      // under Travel). VCCs are the payment instrument for non-DerbySoft
      // hotels, so grouping with Payment Exceptions / Record Payment /
      // Customer Fees matches the mental model finance staff use daily.
      items.push({
        key: 'vcc-cards',
        label: 'VCCs',
        icon: <CreditCardIcon />,
        path: '/vcc-cards',
        match: (p) => p.startsWith('/vcc-cards'),
      });
      items.push({
        key: 'customer-fees',
        label: 'Customer Fees',
        icon: <ReceiptLongIcon />,
        path: '/customer-fees',
        match: (p) => p.startsWith('/customer-fees'),
      });
    }
    sections.push({ key: 'finance', label: 'Finance', items });
  }

  // 6. Reporting — analytics and printable/exportable reports.
  if (isAdmin || isMsiStaff || isTravelMgr) {
    sections.push({
      key: 'reporting',
      label: 'Reporting',
      items: [
        {
          key: 'reports',
          label: 'Reports',
          icon: <AssessmentOutlinedIcon />,
          path: '/reports',
          match: (p) => p.startsWith('/reports'),
        },
      ],
    });
  }

  // 7. Directory — entity records you maintain
  {
    const items = [];
    if (isAdmin || isMsiStaff) {
      items.push({
        key: 'customers',
        label: 'Customers',
        icon: <BusinessOutlinedIcon />,
        path: '/customers',
        match: (p) => p.startsWith('/customers'),
      });
    }
    // Vendor management is MSI-only. Travel Managers don't see vendors as a
    // directory — they only interact with vendors implicitly through the
    // booking flow (which uses separate `derbysoft-*` endpoints).
    if (isAdmin || isMsiStaff) {
      items.push({
        key: 'vendors',
        label: 'Vendors',
        icon: <AddBusinessIcon />,
        path: '/vendors',
        match: (p) =>
          p === '/vendors' ||
          (p.startsWith('/vendors/') && !p.startsWith('/vendors/derby-status')),
      });
    }
    if (isAdmin) {
      items.push({
        key: 'chains',
        label: 'Hotel Chains',
        icon: <AccountTreeIcon />,
        path: '/chains',
        match: (p) => p.startsWith('/chains'),
      });
    }
    if (isAdmin || isTravelMgr) {
      items.push({
        key: 'employees',
        label: 'Employees',
        icon: <SupervisedUserCircleIcon />,
        path: '/employees',
        match: (p) => p.startsWith('/employees'),
      });
    }
    if (items.length) sections.push({ key: 'directory', label: 'Directory', items });
  }

  // 8. System — true admin / configuration
  if (isAdmin) {
    sections.push({
      key: 'system',
      label: 'System',
      items: [
        {
          key: 'users',
          label: 'User Management',
          icon: <ManageAccountsIcon />,
          path: '/users',
          match: (p) => p.startsWith('/users'),
        },
        {
          key: 'derby-status',
          label: 'Derby Status',
          icon: <ToggleOnIcon />,
          path: '/vendors/derby-status',
          match: (p) => p.startsWith('/vendors/derby-status'),
        },
        {
          key: 'integrations',
          label: 'Integrations',
          icon: <IntegrationInstructions />,
          path: '/integrations',
          match: (p) => p.startsWith('/integrations'),
        },
        {
          key: 'policies',
          label: 'Policies',
          icon: <GavelOutlinedIcon />,
          path: '/policies',
          match: (p) => p.startsWith('/policies'),
        },
        {
          key: 'tags',
          label: 'Tags',
          icon: <LabelOutlinedIcon />,
          path: '/tags',
          match: (p) => p.startsWith('/tags'),
        },
        // Seeder is Super Admin only — bootstrap/emergency tooling for
        // populating demo billing data without SSH'ing to the server.
        // Hidden from Admin and MSI Staff since they don't need it.
        ...(isSuperAdmin ? [{
          key: 'seeder',
          label: 'Data Seeder',
          icon: <ScienceOutlinedIcon />,
          path: '/admin/seeder',
          match: (p) => p.startsWith('/admin/seeder'),
        }] : []),
      ],
    });
  }

  const visibleSections = sections.filter((section) => section.items.length);
  const findActiveSectionKey = () => {
    const active = visibleSections.find((section) =>
      section.items.some((item) => {
        if (!item.path) return false;
        if (item.match) return item.match(location.pathname);
        if (item.exact) return location.pathname === item.path;
        return location.pathname.startsWith(item.path);
      })
    );
    return active?.key ?? visibleSections[0]?.key ?? null;
  };
  const [openSections, setOpenSections] = React.useState(() => {
    const initialKey = findActiveSectionKey();
    return initialKey ? [initialKey] : [];
  });
  const prevPathRef = React.useRef(location.pathname);

  React.useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      const nextKey = findActiveSectionKey();
      prevPathRef.current = location.pathname;
      if (nextKey) {
        setOpenSections((prev) => (prev.includes(nextKey) ? prev : [...prev, nextKey]));
      }
    }
  }, [location.pathname, visibleSections]);

  const NavItem = ({ item }) => {
    const {
      icon,
      label,
      path,
      match,
      disabled = false,
      loading = false,
      exact = false,
    } = item;

    const isSelected = match
      ? match(location.pathname)
      : exact
      ? location.pathname === path
      : path
      ? location.pathname.startsWith(path)
      : false;

    const handleNavigate = () => {
      if (disabled || !path) return;
      navigate(path);
      if (isMobile) {
        handleDrawerToggle();
      }
    };

    const button = (
      <ListItemButton
        onClick={handleNavigate}
        disabled={disabled}
        selected={isSelected}
        sx={{
          borderRadius: 2,
          mx: isExpanded ? 1.25 : 1,
          px: isExpanded ? 2 : 1,
          minHeight: 42,
          justifyContent: isExpanded ? 'flex-start' : 'center',
          transition: 'background-color 0.25s ease, padding 0.25s ease',
          color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
          backgroundColor: isSelected
            ? alpha(theme.palette.primary.main, 0.12)
            : 'transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
          '&.Mui-selected:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.16),
          },
          '&.Mui-disabled': {
            opacity: 0.5,
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: isExpanded ? 1.5 : 0,
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: isSelected
                ? alpha(theme.palette.primary.main, 0.16)
                : alpha(theme.palette.primary.main, 0.08),
              boxShadow: isSelected ? '0 6px 14px rgba(15, 23, 42, 0.12)' : 'none',
              transition: 'all 0.2s ease',
              color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
              '& svg': {
                fontSize: 20,
              },
            }}
          >
            {icon}
          </Box>
        </ListItemIcon>
        <ListItemText
          primary={loading ? <Skeleton variant="text" width={80} /> : label}
          sx={{
            opacity: isExpanded ? 1 : 0,
            maxWidth: isExpanded ? 180 : 0,
            transition: 'opacity 0.2s ease, max-width 0.2s ease',
            overflow: 'hidden',
          }}
          slotProps={{ primary: { variant: 'body2', fontWeight: 600, color: 'inherit' } }}
        />
      </ListItemButton>
    );

    if (isExpanded) {
      return button;
    }

    return (
      <Tooltip title={label} placement="right">
        <span>{button}</span>
      </Tooltip>
    );
  };

  const SectionHeader = ({ section }) => {
    if (!isExpanded || !section.label) {
      return null;
    }

    const isOpen = openSections.includes(section.key);

    const handleToggle = () => {
      setOpenSections((prev) =>
        prev.includes(section.key)
          ? prev.filter((key) => key !== section.key)
          : [...prev, section.key]
      );
    };

    return (
      <ListItemButton
        onClick={handleToggle}
        sx={{
          borderRadius: 2,
          mb: 0.5,
          px: 1.5,
          py: 0.75,
          minHeight: 36,
          color: 'text.secondary',
          backgroundColor: isOpen
            ? alpha(theme.palette.primary.main, 0.08)
            : 'transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
          },
          '& .section-toggle': {
            opacity: 0,
            transition: 'opacity 0.2s ease',
          },
          '&:hover .section-toggle': {
            opacity: 1,
          },
        }}
        aria-expanded={isOpen}
      >
        <ListItemText
          primary={section.label}
          slotProps={{ primary: { variant: 'caption', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' } }}
        />
        <Box className="section-toggle" sx={{ display: 'flex', alignItems: 'center' }}>
          {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>
      </ListItemButton>
    );
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={handleDrawerToggle}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          border: 'none',
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.9)
              : alpha(theme.palette.background.paper, 0.96),
          backgroundImage: 'none',
          boxShadow: 'none',
          borderRight: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          position: 'relative',
          height: '100%',
          transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
          }),
          // Allow the floating expand/collapse chevron to overhang
          // the right edge of the drawer.
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ px: isExpanded ? 1.5 : 1, position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            justifyContent: isExpanded ? 'flex-start' : 'center',
            p: isExpanded ? 1.5 : 1,
            borderRadius: 2,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.88)
                : alpha(theme.palette.background.paper, 0.98),
          }}
        >
          <Box
            sx={{
              width: isExpanded ? 46 : 36,
              height: isExpanded ? 46 : 36,
              borderRadius: 2.5,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={logoSrc}
              alt="MSI Logo"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                p: isExpanded ? 0.6 : 0.5,
              }}
            />
          </Box>
          {isExpanded && (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                MSI Lodging
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                Reservations
              </Typography>
            </Box>
          )}
        </Box>

        {/* Desktop expand/collapse toggle — sits on the drawer's right edge so
            it's always discoverable without crowding the header content. */}
        {!isMobile && onToggleExpand && (
          <Tooltip title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'} placement="right">
            <IconButton
              onClick={onToggleExpand}
              size="small"
              aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              sx={{
                position: 'absolute',
                top: '50%',
                right: -14,
                transform: 'translateY(-50%)',
                width: 28,
                height: 28,
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.text.secondary,
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
                zIndex: 2,
                transition: 'all 180ms ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              {isExpanded
                ? <ChevronLeftIcon fontSize="small" />
                : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Divider sx={{mx: isExpanded ? 2 : 0, opacity: 0.4 }} />
      <List
        component="nav"
        sx={{
          py: 2,
          flexGrow: 1,
          gap: 0.5,
          display: 'flex',
          flexDirection: 'column',
          px: isExpanded ? 1.5 : 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          // Always reserve gutter space so labels don't reflow when the
          // scrollbar fades in/out. Only the thumb color is toggled — the
          // track width stays the same.
          scrollbarWidth: 'thin',        // Firefox: thin gutter, always
          scrollbarColor: 'transparent transparent', // hide thumb by default
          '&::-webkit-scrollbar': { width: 6, backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'transparent',
            borderRadius: 4,
            transition: 'background-color 0.2s ease',
          },
          '&:hover': {
            scrollbarColor: `${alpha(theme.palette.divider, 0.7)} transparent`,
            '&::-webkit-scrollbar-thumb': {
              background: alpha(theme.palette.divider, 0.7),
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: alpha(theme.palette.text.secondary, 0.5),
            },
          },
        }}
      >
        {visibleSections.map((section, index) => (
          <Box
            component="div"
            key={section.key}
            sx={{ mt: index === 0 ? 0 : 1.5 }}
          >
            <SectionHeader section={section} />
            {isExpanded ? (
              <Collapse in={openSections.includes(section.key)} timeout="auto" unmountOnExit>
                {section.items.map((item, itemIndex) => (
                  <Box
                    key={item.key}
                    sx={{ mb: itemIndex < section.items.length - 1 ? 0.75 : 0 }}
                  >
                    <NavItem item={item} />
                  </Box>
                ))}
              </Collapse>
            ) : (
              section.items.map((item, itemIndex) => (
                <Box
                  key={item.key}
                  sx={{ mb: itemIndex < section.items.length - 1 ? 0.75 : 0 }}
                >
                  <NavItem item={item} />
                </Box>
              ))
            )}
            {index < visibleSections.length - 1 && (
              <Divider sx={{ mt: isExpanded ? 1.5 : 1, opacity: 0.35 }} />
            )}
          </Box>
        ))}
      </List>

      <Box
        sx={{
          mt: 'auto',
          px: isExpanded ? 1.5 : 1,
          pb: 2,
          pt: 1,
        }}
      >
        <Divider/>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: isExpanded ? 1.25 : 1,
            borderRadius: 2,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.9)
                : alpha(theme.palette.background.paper, 0.98),
          }}
        >
          <UserAvatar
            name={`${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || 'User'}
            size={isExpanded ? 56 : 42}
          />
          {isExpanded && (
            // flex: 1 + minWidth: 0 lets the box shrink below the email's
            // natural width; overflow: hidden gives noWrap somewhere to
            // truncate against. Without these, long emails escape the
            // sidebar's right edge.
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {user?.first_name || 'User'} {user?.last_name || ''}
              </Typography>
              <Tooltip title={user?.email || ''} placement="top" arrow disableHoverListener={!user?.email}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {user?.email || 'user@example.com'}
                </Typography>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default Aside;